// Qué parte del historial le pide Contabilidad al servidor.
//
// Motivo (producción, 04-oct-2026, Sentry): Transactions.jsx pedía el historial ENTERO del
// club para filtrar por fechas, sumar los chips y generar el Excel en el navegador. Con la
// tabla creciendo, esa llamada tardaba ~19 s de mediana por la noche y, como serializarla es
// trabajo síncrono del worker del backend, congelaba también las demás peticiones.
//
// Ahora el servidor recibe la ventana (`date_from`/`date_to`) y el navegador sigue filtrando
// DENTRO de lo recibido (tipo, pago, búsqueda y recorte fino de fechas). Chips y Excel siguen
// saliendo de lo filtrado, que es lo que fija la ficha (saldo-pagos-caja.md#filtros-transacciones).
//
// Un dato, un sitio: la misma cota de fecha sirve para recortar en el navegador y para pedirla
// al servidor, así que las dos no pueden discrepar.

const MS_DIA = 24 * 60 * 60 * 1000;

/**
 * Periodo con el que se abre Contabilidad: la JORNADA de hoy del club (decidido 09-10-2026).
 * Antes eran los últimos 30 días (04-10-2026): en Kong Club eso son ~6,6 MB y 3-4 s en la
 * tablet cada vez que se entra, para una pantalla que al cerrar mira el turno de hoy. La jornada
 * respeta la hora de corte del club (Kong abre a las 11:00 y cierra a la 01:00-02:00: con corte
 * 06:00 el turno entero cae en su día); con corte 00:00 es el día natural.
 */
export const PERIODO_INICIAL = "jornada";

/**
 * La jornada del club que contiene `ahora`, desplazada `desplazamientoDias` hacia atrás
 * (0 = la de hoy, 1 = la de ayer): `[corte, corte + 24 h)`, expresada como `desde` y un `hasta`
 * INCLUSIVO (el último milisegundo antes del siguiente corte, que es lo que entiende el servidor
 * en `date_to`). De madrugada, antes del corte, «hoy» es todavía la jornada que empezó ayer.
 *
 * UNA sola definición: la usan el periodo inicial, el recorte en el navegador y los botones
 * «Jornada de hoy / de ayer». Con un corte fuera de 0-23 (o sin él) se usa el día natural.
 */
/** La hora de corte como la ve el club: `06:00`. Un dato, un render (aviso y etiqueta del corte). */
export function horaDeCorte(corte) {
  return `${String(corteValido(corte)).padStart(2, "0")}:00`;
}

function corteValido(corte) {
  return Number.isInteger(corte) && corte >= 0 && corte <= 23 ? corte : 0;
}

export function ventanaDeJornada(corte, desplazamientoDias = 0, ahora = new Date()) {
  const horaCorte = corteValido(corte);
  const desde = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate(), horaCorte, 0, 0, 0);
  if (ahora < desde) desde.setDate(desde.getDate() - 1);
  desde.setDate(desde.getDate() - desplazamientoDias);
  // En campos LOCALES (setDate), no sumando 24 h en ms: las dos jornadas del año con cambio de
  // horario duran 23 o 25 h y el corte se iría ±1 h.
  const hasta = new Date(desde);
  hasta.setDate(hasta.getDate() + 1);
  hasta.setMilliseconds(hasta.getMilliseconds() - 1);
  return { desde, hasta };
}

/**
 * Cota de un `<input type="datetime-local">` (o de una fecha suelta, valor antiguo).
 * `boundary` = "start" | "end". Con hora, el fin llega al último milisegundo de ese minuto;
 * sin hora, al final del día.
 */
export function cotaDeFecha(value, boundary) {
  if (!value) return null;
  // Horquilla horaria (petición Kong Club): el club abre a las 11:00 y cierra a la 01:00-02:00
  // del día siguiente — con días naturales el filtro mezcla dos turnos.
  const hasTime = value.includes("T");
  const parsed = new Date(hasTime ? value : `${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  if (boundary === "end") {
    if (hasTime) parsed.setSeconds(59, 999);
    else parsed.setHours(23, 59, 59, 999);
  }
  return parsed;
}

/**
 * Límite inferior de un periodo predefinido (Jornada de hoy / Última semana / Último mes), o
 * `null` si no acota («Todas las fechas»). UNA sola definición: la usan el recorte del navegador
 * y la ventana que se pide al servidor, así que no pueden discrepar (review). «Hoy» como día
 * natural desapareció el 09-10-2026: en los clubes que cierran de madrugada mezclaba dos turnos;
 * la jornada con corte 00:00 ES el día natural.
 */
export function desdeDePreset(filterDate, now = new Date(), corte = 0) {
  switch (filterDate) {
    case "jornada":
      return ventanaDeJornada(corte, 0, now).desde;
    case "week":
      return new Date(now.getTime() - 7 * MS_DIA);
    case "month":
      return new Date(now.getTime() - 30 * MS_DIA);
    default:
      return null;
  }
}

/**
 * Parámetros `date_from`/`date_to` (ISO, instantes absolutos: no dependen de la zona del
 * navegador) que cubren lo que la pantalla puede llegar a mostrar con ese filtro.
 * `{}` = sin ventana = historial completo, que ahora es una elección explícita
 * («Todas las fechas»), no el arranque.
 *
 * Un rango personalizado manda sobre el preset; el selector de periodo ya borra el rango al
 * cambiarse, así que en la práctica no coexisten.
 */
export function ventanaDeCarga({ filterDate, customFrom, customTo, corte = 0, now = new Date() }) {
  const desde = cotaDeFecha(customFrom, "start");
  const hasta = cotaDeFecha(customTo, "end");
  if (desde || hasta) {
    return {
      ...(desde && { date_from: desde.toISOString() }),
      ...(hasta && { date_to: hasta.toISOString() }),
    };
  }

  if (filterDate === "jornada") {
    // Con las dos cotas: la jornada tiene fin (el siguiente corte), aunque hoy esté en el futuro.
    const jornada = ventanaDeJornada(corte, 0, now);
    return { date_from: jornada.desde.toISOString(), date_to: jornada.hasta.toISOString() };
  }

  const limite = desdeDePreset(filterDate, now, corte);
  return limite ? { date_from: limite.toISOString() } : {};
}

/** Query string de la petición (`venue_id` + ventana); sin parámetros devuelve "". */
export function queryDeTransacciones(venueID, ventana) {
  const params = new URLSearchParams();
  if (venueID) params.set("venue_id", venueID);
  if (ventana.date_from) params.set("date_from", ventana.date_from);
  if (ventana.date_to) params.set("date_to", ventana.date_to);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}
